import React, { useEffect, useState, useRef } from 'react';
import { auth, db, storage } from '../../../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { GoogleMap, Autocomplete } from '@react-google-maps/api';
import './MyEvents.css';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';
import { placeholder } from '../../../photos/index.js';
import { TranslatedText } from '../../i18n/TranslatedText.jsx';
import { useTranslatedText } from '../../i18n/useTranslatedText.js';

const MediaItem = ({ file, index, moveMedia, removeMedia }) => {
    const [, ref] = useDrag({
      type: 'media',
      item: { index },
    });
  
    const [, drop] = useDrop({
      accept: 'media',
      hover: (draggedItem) => {
        if (draggedItem.index !== index) {
          moveMedia(draggedItem.index, index);
          draggedItem.index = index;
        }
      },
    });
  
    return (
      <div ref={(node) => ref(drop(node))} className="media-items">
        {typeof file === 'string' ? (
          file.includes('mp4') ? (
            <video
              src={file}
              className="preview-media"
              controls
              autoPlay={false} // Disable autoplay if necessary
              muted={false}
              onCanPlayThrough={(e) => {
                e.target.muted = false; // Ensure audio is enabled on play
              }}
            />
          ) : (
            <img src={file} alt="preview" className="preview-media" />
          )
        ) : (
          file.type.startsWith('video') ? (
            <video
              src={URL.createObjectURL(file)}
              className="preview-media"
              controls
              muted
              onLoadedMetadata={(e) => {
                e.target.muted = false; // Ensure video plays with sound
              }}
            />
          ) : (
            <img 
              src={URL.createObjectURL(file)} 
              alt="preview" 
              className="preview-media" 
            />
          )
        )}
        <button 
          type="button" 
          className="remove-media-button" 
          onClick={() => removeMedia(index)}
        >
          ×
        </button>
      </div>
    );
  };

const MyEvents = () => {
    const [events, setEvents] = useState([]);
    const [registeredEvents, setRegisteredEvents] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null);
    const [autocomplete, setAutocomplete] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [editingMedia, setEditingMedia] = useState([]);
    const loadingText = useTranslatedText('Yükleniyor...');
    const myEventsTitle = useTranslatedText('Etkinliklerim');
    const myRegisteredEventsTitle = useTranslatedText('Kaydolduğunuz Etkinlikler');
    const locationLabel = useTranslatedText('Konum:');
    const dateLabel = useTranslatedText('Tarih:');
    const capacityLabel = useTranslatedText('Kontenjan Sınırı:');
    const ageRangeLabel = useTranslatedText('Yaş Aralığı:');
    const organizerLabel = useTranslatedText('Organizatör:');
    const eventStatusLabel = useTranslatedText('Etkinlik Durumu:');
    const pendingStatus = useTranslatedText('Beklemede');
    const approvedStatus = useTranslatedText('Onaylandı');
    const viewText = useTranslatedText('Görüntüle');
    const registeredUsersText = useTranslatedText('Kayıtlı Kişileri Görüntüleyin');
    const editText = useTranslatedText('Düzenle');
    const deleteText = useTranslatedText('Sil');
    const editEventTitle = useTranslatedText('Etkinlik Düzenle');
    const mediaLabel = useTranslatedText('Medya:');
    const eventNameLabel = useTranslatedText('Etkinlik Adı:');
    const organizerEditLabel = useTranslatedText('Organizatör:');
    const locationEditLabel = useTranslatedText('Konum:');
    const dateEditLabel = useTranslatedText('Tarih:');
    const capacityEditLabel = useTranslatedText('Kontenjan Sınırı:');
    const ageRangeEditLabel = useTranslatedText('Yaş Aralığı:');
    const allAgesText = useTranslatedText('Her Yaşa Uygundur');
    const contactInfoLabel = useTranslatedText('İletişim Bilgisi:');
    const descriptionLabel = useTranslatedText('Açıklama:');
    const registrationsClosedLabel = useTranslatedText('Kayıtlar Kapalı:');
    const saveChangesText = useTranslatedText('Değişiklikleri Kaydet');
    const deleteConfirmTitle = useTranslatedText('Bu Etkinliği Silmek İstediğinize Emin Misiniz?');
    const confirmDeleteText = useTranslatedText('Evet, Kaldırın');
    const cancelText = useTranslatedText('İptal');
    const deleteErrorText = useTranslatedText('Etkinlik silinirken bir hata oluştu. Lütfen tekrar deneyin.');
    const updateSuccessText = useTranslatedText('Etkinlik başarıyla güncellendi!');
    const updateFailedPrefixText = useTranslatedText('Etkinlik güncellenemedi');
    const loginRequiredUpdateText = useTranslatedText('Etkinliği güncellemek için giriş yapmalısınız.');

    const normalizeEditableEvent = (event) => ({
        ...event,
        name: event?.name ?? '',
        organizer: event?.organizer ?? '',
        location: event?.location ?? '',
        coordinates: event?.coordinates ?? null,
        date: event?.date ?? '',
        maxCapacity: event?.maxCapacity ?? '',
        ageRange: event?.ageRange ?? 'all',
        contactInfo: event?.contactInfo ?? '',
        description: event?.description ?? '',
        registrationDisabled: Boolean(event?.registrationDisabled),
    });

    useEffect(() => {
        const fetchEvents = async () => {
          const user = auth.currentUser;
          if (user) {
            await loadEvents(user.uid);
          } else {
            const unsubscribe = auth.onAuthStateChanged(async (newUser) => {
              if (newUser) {
                await loadEvents(newUser.uid);
                unsubscribe();
              }
            });
          }
        };
      
        fetchEvents();
      }, []);

    const loadEvents = async (uid) => {
        const q = query(collection(db, 'events'), where('createdBy', '==', uid));
        const querySnapshot = await getDocs(q);
        setEvents(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
            const registeredEventIds = userSnap.data().registeredEvents || [];
            const registeredEventsData = await Promise.all(
                registeredEventIds.map(async (eventId) => {
                    try {
                        const eventDoc = await getDoc(doc(db, 'events', eventId));
                        if (!eventDoc.exists()) return null;
                        return { id: eventDoc.id, ...eventDoc.data() };
                    } catch (error) {
                        console.error('Error fetching registered event:', eventId, error);
                        return null;
                    }
                })
            );

            setRegisteredEvents(registeredEventsData.filter(Boolean));

            const missingEventIds = registeredEventIds.filter((eventId, idx) => registeredEventsData[idx] == null);
            if (missingEventIds.length > 0) {
                try {
                    await updateDoc(userRef, {
                        registeredEvents: registeredEventIds.filter((eventId) => !missingEventIds.includes(eventId)),
                    });
                } catch (error) {
                    console.error('Error cleaning up missing registered events:', error);
                }
            }
        }
        setLoading(false);
    };
    
    useEffect(() => {
        return () => {
            if (markerRef.current) {
                markerRef.current.setMap(null);
            }
            mapRef.current = null;
        };
    }, []);

    const handleDelete = (eventId) => {
        setDeleteConfirmation(eventId);
    };

    const confirmDelete = async () => {
        try {
            // Get the event data before deletion
            const eventDoc = await getDoc(doc(db, 'events', deleteConfirmation));
            if (!eventDoc.exists()) {
                throw new Error('Event not found');
            }
            const eventData = eventDoc.data();
    
            // Delete all media files from storage
            if (eventData.mediaUrls && eventData.mediaUrls.length > 0) {
                await Promise.all(eventData.mediaUrls.map(async (url) => {
                    try {
                        const fileRef = ref(storage, url);
                        await deleteObject(fileRef);
                    } catch (error) {
                        console.error('Error deleting file:', error);
                        // Continue with deletion even if a file fails to delete
                    }
                }));
            }
    
            // Delete thumbnail if it's different from mediaUrls (or if mediaUrls is missing)
            if (eventData.thumbnailUrl && (!Array.isArray(eventData.mediaUrls) || !eventData.mediaUrls.includes(eventData.thumbnailUrl))) {
                try {
                    const thumbnailRef = ref(storage, eventData.thumbnailUrl);
                    await deleteObject(thumbnailRef);
                } catch (error) {
                    console.error('Error deleting thumbnail:', error);
                    // Continue with deletion even if thumbnail fails to delete
                }
            }
    
            // Finally delete the event document
            await deleteDoc(doc(db, 'events', deleteConfirmation));
            
            setEvents(events.filter(event => event.id !== deleteConfirmation));
            setDeleteConfirmation(null);
    
        } catch (error) {
            console.error('Error during event deletion:', error);
            alert(deleteErrorText);
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(normalizeEditableEvent(event));
        setEditingMedia(Array.isArray(event?.mediaUrls) ? event.mediaUrls : []);
    };

    const handleMediaChange = (e) => {
        const files = Array.from(e.target.files);
        setEditingMedia((prevMedia) => [...prevMedia, ...files]);
      };

    const handleRemoveMedia = (index) => {
        setEditingMedia((prevMedia) => prevMedia.filter((_, i) => i !== index));
    };

    const handleReorderMedia = (fromIndex, toIndex) => {
        setEditingMedia((prevMedia) => {
          const updatedMedia = [...prevMedia];
          const [movedItem] = updatedMedia.splice(fromIndex, 1);
          updatedMedia.splice(toIndex, 0, movedItem);
          return updatedMedia;
        });
      };

      const handleUpdate = async (e) => {
        e.preventDefault();
        try {
          const user = auth.currentUser;
          if (!user) {
            throw new Error(loginRequiredUpdateText);
          }

          const eventId = editingEvent.id;
          const sanitizedEventName = (editingEvent.name || '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
          const eventDirectory = `events/${eventId}_${sanitizedEventName}`;
       
          // Handle media files
          const previousMediaUrls = Array.isArray(editingEvent.mediaUrls) ? editingEvent.mediaUrls : [];
          const keptExistingUrls = new Set(editingMedia.filter((item) => typeof item === 'string'));
          const removedMedia = previousMediaUrls.filter((url) => !keptExistingUrls.has(url));

          const mediaUrls = [];
          
          for (let file of editingMedia) {
            if (file instanceof File) {
              // New file upload
              const uniqueFileName = `${uuidv4()}_${file.name}`;
              const fileRef = ref(storage, `${eventDirectory}/${uniqueFileName}`);
              await uploadBytes(fileRef, file, {
                contentType: file.type || 'application/octet-stream',
              });
              const url = await getDownloadURL(fileRef);
              mediaUrls.push(url);
            } else if (typeof file === 'string') {
              mediaUrls.push(file);
            }
          }

          for (let url of removedMedia) {
            try {
                const fileRef = ref(storage, url);
                await deleteObject(fileRef);
            } catch (error) {
                console.error('Error deleting removed media:', error);
            }
        }

          const nextThumbnailUrl =
            editingEvent.thumbnailUrl && mediaUrls.includes(editingEvent.thumbnailUrl)
              ? editingEvent.thumbnailUrl
              : mediaUrls[0] || '';

          const updatePayload = {
            name: editingEvent.name,
            location: editingEvent.location,
            coordinates: editingEvent.coordinates ?? null,
            date: editingEvent.date,
            maxCapacity: editingEvent.maxCapacity,
            ageRange: editingEvent.ageRange,
            description: editingEvent.description,
            mediaUrls,
            thumbnailUrl: nextThumbnailUrl,
            organizer: editingEvent.organizer,
            contactInfo: editingEvent.contactInfo,
            registrationDisabled: Boolean(editingEvent.registrationDisabled),
          };

          // Firestore rejects `undefined` values. For older docs that may be missing fields,
          // omit undefined keys and let existing values remain unchanged.
          Object.keys(updatePayload).forEach((key) => {
            if (updatePayload[key] === undefined) {
              delete updatePayload[key];
            }
          });

          await updateDoc(doc(db, 'events', eventId), updatePayload);
      
          // Update the local state
          setEvents(events.map(event => (event.id === eventId ? { ...event, ...updatePayload } : event)));
          
          setEditingEvent(null);
          setEditingMedia([]);
      
          alert(updateSuccessText);
      
        } catch (error) {
          console.error('Error updating event:', error);
          const code = error?.code ? String(error.code) : '';
          const message = error?.message ? String(error.message) : String(error);
          alert(`${updateFailedPrefixText}${code ? ` (${code})` : ''}: ${message}`);
        }
      };

    const handlePlaceSelect = () => {
      if (autocomplete) {
          const place = autocomplete.getPlace();
          if (place.geometry && place.geometry.location) {
              const newCoordinates = {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
              };
              setEditingEvent(prev => ({
                  ...prev,
                  location: place.formatted_address,
                  coordinates: newCoordinates
              }));
  
              if (mapRef.current) {
                  mapRef.current.panTo(newCoordinates);
              }
          }
      }
    };

    useEffect(() => {
      if (editingEvent && mapRef.current && window.google) {
          if (markerRef.current) {
              markerRef.current.setMap(null);
          }
  
          markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
              position: editingEvent.coordinates,
              map: mapRef.current
          });
  
          mapRef.current.panTo(editingEvent.coordinates);
      }
  }, [editingEvent]);

    if (loading) {
            return <div>{loadingText}</div>;
        }

    return (
        <div className="my-events">
            <h2>{myEventsTitle}</h2>
            {events.map((event) => (
                <div key={event.id} className="event-item">
                    <div className="event-thumbnail">
                        <img src={event.thumbnailUrl || placeholder} alt={event.name} />
                    </div>
                    <div className="event-content">
                        <h3><TranslatedText text={event.name} dynamic /></h3>
                        <div className="event-info">
                            <p><span>{locationLabel}</span> <TranslatedText text={event.location} dynamic /></p>
                            <p><span>{dateLabel}</span> {event.date}</p>
                            <p><span>{capacityLabel}</span> {event.maxCapacity}</p>
                            <p><span>{ageRangeLabel}</span> {event.ageRange}</p>
                            <p><span>{organizerLabel}</span> <TranslatedText text={event.organizer} dynamic /></p>
                            <div className="status-container">
                            <span>{eventStatusLabel} </span>
                                {event.status.toLowerCase() === 'pending' && (
                                <span className={`status-badge ${event.status.toLowerCase()}`}>
                                    <i className="fas fa-clock"></i> {pendingStatus}
                                </span>
                                )}
                                {event.status.toLowerCase() === 'approved' && (
                                <span className="status-badge approved">
                                    <i className="fas fa-check"></i> {approvedStatus}
                                </span>
                                )}
                            </div>
                        </div>
                        <div className="event-actions">
                            <Link to={`/etkinlik/${event.id}`} className="btn btn-view">{viewText}</Link>
                            <Link to={`/kayıtlı-kişiler/${event.id}`} className="btn btn-view">{registeredUsersText}</Link>
                            <button className="btn btn-edit" onClick={() => handleEdit(event)}>{editText}</button>
                            <button className="btn btn-delete" onClick={() => handleDelete(event.id)}>{deleteText}</button>
                        </div>
                    </div>
                </div>
            ))}
            {editingEvent && (
                <div className="edit-event-overlay">
                    <div className="edit-event-form">
                        <div className="edit-form-header">
                            <h2>{editEventTitle}</h2>
                            <button className="close-button" onClick={() => setEditingEvent(null)}>×</button>
                        </div>

                        <div className="form-group">
                            
                        <label className="media-upload-label">{mediaLabel}</label>
                        <input 
                        type="file" 
                        multiple 
                        onChange={handleMediaChange}
                        className="media-upload-input"
                        accept="image/*,video/*"
                        />
                        <DndProvider backend={HTML5Backend}>
                        <div className="media-previews">
                            {editingMedia.map((media, index) => (
                            <MediaItem
                                key={index}
                                file={media}
                                index={index}
                                moveMedia={handleReorderMedia}
                                removeMedia={handleRemoveMedia}
                            />
                            ))}
                        </div>
                        </DndProvider>
                    </div>

                        
                        <form onSubmit={handleUpdate}>
                            <div className="form-group">
                                <label>{eventNameLabel}</label>
                                <input 
                                    type="text" 
                                    value={editingEvent.name} 
                                    onChange={(e) => setEditingEvent({...editingEvent, name: e.target.value})} 
                                />
                            </div>
                            <div className="form-group">
                                <label>{organizerEditLabel}</label>
                                <input 
                                    type="text" 
                                    value={editingEvent.organizer} 
                                    onChange={(e) => setEditingEvent({...editingEvent, organizer: e.target.value})} 
                                />
                            </div>
                            <div className="form-group">
                                <label>{locationEditLabel}</label>
                                <Autocomplete
                                    onLoad={setAutocomplete}
                                    onPlaceChanged={handlePlaceSelect}
                                >
                                    <input 
                                        type="text" 
                                        value={editingEvent.location} 
                                        onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
                                    />
                                </Autocomplete>
                            </div>
                            <div className="form-group map-container">
                            <GoogleMap
                              onLoad={(map) => {
                                  mapRef.current = map;
                                  if (window.google && editingEvent?.coordinates) {
                                      try {
                                          markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
                                              position: editingEvent.coordinates,
                                              map: map
                                          });
                                      } catch (error) {
                                          console.error('Error creating marker:', error);
                                      }
                                  }
                              }}
                              center={editingEvent.coordinates}
                              zoom={15}
                              mapContainerClassName="map"
                              options={{
                                mapId: "6100ebed9733d3ab",
                              }}
                          />
                            </div>
                            <div className="form-group">
                                <label>{dateEditLabel}</label>
                                <input 
                                    type="date" 
                                    value={editingEvent.date} 
                                    onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})} 
                                />
                            </div>
                            <div className="form-group">
                                <label>{capacityEditLabel}</label>
                                <input 
                                    type="number" 
                                    value={editingEvent.maxCapacity} 
                                    onChange={(e) => setEditingEvent({...editingEvent, maxCapacity: e.target.value})} 
                                />
                            </div>
                            <div className="form-group">
                                <label>{ageRangeEditLabel}</label>
                                <select 
                                    className='value-select'
                                    value={editingEvent.ageRange} 
                                    onChange={(e) => setEditingEvent({...editingEvent, ageRange: e.target.value})}
                                >
                                    <option value="all">{allAgesText}</option>
                                    <option value="0-12">0-12</option>
                                    <option value="13-17">13-17</option>
                                    <option value="18+">18+</option>
                                    <option value=" 21+">21+</option>
                                </select>
                            </div>
                                
                        <div className="form-group">
                            <label>{contactInfoLabel}</label>
                            <textarea 
                                value={editingEvent.contactInfo} 
                                onChange={(e) => setEditingEvent({...editingEvent, contactInfo: e.target.value})}
                            />
                        </div>

                        <div className="form-group">
                            <label>{descriptionLabel}</label>
                            <textarea 
                                value={editingEvent.description} 
                                onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})}
                            />
                        </div>

                        <div className="form-group">
                            <label><span>{registrationsClosedLabel}</span>
                            <input 
                                type="checkbox" 
                                checked={editingEvent.registrationDisabled} 
                                onChange={(e) => setEditingEvent({...editingEvent, registrationDisabled: e.target.checked})}
                            /></label>
                        </div>


                            <button type="submit" className="btn btn-save">{saveChangesText}</button>
                        </form>
                    </div>
                </div>
            )}
            {deleteConfirmation && (
              <div className="delete-confirmation-overlay">
                  <div className="delete-confirmation">
                      <h2>{deleteConfirmTitle}</h2>
                      <div className="btn-group">
                          <button className="btn-confirm" onClick={confirmDelete}>
                              {confirmDeleteText}
                          </button>
                          <button className="btn-cancel" onClick={() => setDeleteConfirmation(null)}>
                              {cancelText}
                          </button>
                      </div>
                  </div>
              </div>
              )}

            <h2>{myRegisteredEventsTitle}</h2>
            <div className="registered-events">
                {registeredEvents.map((event) => (
                    <div key={event.id} className="registered-event-item">
                        <div className="event-thumbnail">
                            <img src={event.thumbnailUrl || placeholder} alt={event.name} />
                        </div>
                        <div className="event-content">
                            <h3><TranslatedText text={event.name} dynamic /></h3>
                            <div className="event-info">
                                <p><span>{locationLabel}</span> <TranslatedText text={event.location} dynamic /></p>
                                <p><span>{dateLabel}</span> {event.date}</p>
                                <p><span>{organizerLabel}</span> <TranslatedText text={event.organizer} dynamic /></p>
                                <p><span>{capacityLabel}</span> {event.maxCapacity}</p>
                                <p><span>{ageRangeLabel}</span> {event.ageRange}</p>
                            </div>
                            <div className="event-actions">
                                <Link to={`/etkinlik/${event.id}`} className="btn-view">{viewText}</Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MyEvents;
